import { UtensilsCrossed, Star, Layers } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

// Items grid - shown after a category is opened OR while searching.
export const PosItemsGrid = ({
  filteredItems,
  categories,
  selectedCategory,
  searchQuery,
  currencySymbol,
  isComfortable,
  isItemScheduleActive,
  onAddItem,
  taxRate = 0,
}) => {
  const { t } = useLanguage();
  const catName = categories.find((c) => c.id === selectedCategory)?.name || 'All Items';
  return (
    <div className="flex-1 px-4 pb-4 overflow-y-auto pb-20 lg:pb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          <Layers size={14} />
          <span data-testid="pos-current-category">
            {searchQuery ? `Search · "${searchQuery}"` : catName}
          </span>
          <span className="text-violet-500">· {filteredItems.length} items</span>
        </div>
      </div>
      <div className={isComfortable
        ? 'grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))] md:[grid-template-columns:repeat(auto-fit,minmax(156px,1fr))] xl:[grid-template-columns:repeat(auto-fit,minmax(176px,1fr))]'
        : 'grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(112px,1fr))] md:[grid-template-columns:repeat(auto-fit,minmax(124px,1fr))] xl:[grid-template-columns:repeat(auto-fit,minmax(140px,1fr))]'}>
        {filteredItems.map((item) => {
          const scheduleInactive = item.schedule?.enabled && !isItemScheduleActive(item);
          const isDisabled = !item.is_available || scheduleInactive;
          return (
            <div
              key={item.id}
              className={`bg-white overflow-hidden cursor-pointer transition-all duration-200 ${isComfortable ? 'rounded-2xl' : 'rounded-xl'} ${
                scheduleInactive ? 'opacity-40 grayscale'
                  : !item.is_available ? 'opacity-50'
                    : 'hover:shadow-lg hover:-translate-y-0.5'
              } shadow-sm`}
              onClick={() => !isDisabled && onAddItem(item)}
              data-testid={`menu-item-${item.id}`}
            >
              {item.image_url ? (
                <img src={item.image_url} alt={item.name} className={`w-full object-cover ${isComfortable ? 'h-24' : 'h-16 sm:h-20'}`} />
              ) : (
                <div className={`w-full bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center ${isComfortable ? 'h-24' : 'h-16 sm:h-20'}`}>
                  <UtensilsCrossed size={isComfortable ? 28 : 22} className="text-muted-foreground" />
                </div>
              )}
              <div className={isComfortable ? 'p-3' : 'p-2'}>
                <h3 className={`font-semibold text-foreground leading-tight line-clamp-2 break-words ${isComfortable ? 'text-[13px]' : 'text-[12px]'}`}>{item.name}</h3>
                {item.name_ar && <p className={`text-muted-foreground line-clamp-1 mt-0.5 ${isComfortable ? 'text-[11px]' : 'text-[10px]'}`} dir="rtl">{item.name_ar}</p>}
                <div className={`flex items-center justify-between min-w-0 gap-1 ${isComfortable ? 'mt-2' : 'mt-1'}`}>
                  {/* VAT-inclusive display price: exclusive items get VAT added on top */}
                  <span className={`font-bold text-primary truncate min-w-0 ${isComfortable ? 'text-sm' : 'text-[12px]'}`} data-testid={`item-price-${item.id}`}>
                    {currencySymbol} {(item.price_includes_tax ? Number(item.price || 0) : Number(item.price || 0) * (1 + taxRate)).toFixed(2)}
                    {taxRate > 0 && isComfortable && <span className="font-normal text-muted-foreground text-[9px] ms-1">{t('incl_vat')}</span>}
                  </span>
                  {item.tags?.includes('popular') && <Star size={isComfortable ? 13 : 11} className="text-amber-400 fill-amber-400 shrink-0" />}
                </div>
                {taxRate > 0 && !isComfortable && <span className="block truncate font-normal text-muted-foreground text-[9px] leading-none">{t('incl_vat')}</span>}
                {item.modifiers?.length > 0 && (
                  <span className={`inline-block bg-muted text-muted-foreground rounded-full ${isComfortable ? 'text-[10px] mt-1.5 px-2 py-0.5' : 'text-[9px] mt-1 px-1.5 py-0.5'}`}>{isComfortable ? 'Has options' : 'Options'}</span>
                )}
                {scheduleInactive && (
                  <span className={`inline-block bg-info/10 text-info rounded-full ${isComfortable ? 'text-[10px] mt-1 px-2 py-0.5' : 'text-[9px] mt-1 px-1.5 py-0.5'}`}>{isComfortable ? 'Not available now' : 'Off-hours'}</span>
                )}
              </div>
            </div>
          );
        })}
        {filteredItems.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            {searchQuery ? `No items match "${searchQuery}".` : 'No items in this category.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default PosItemsGrid;
